"use client";

import * as React from "react";

import { AUTH_CHANGED_EVENT, hasStoredAuthToken } from "@/app/services/auth-storage";
import api from "@/app/services/api";
import { API_URLS } from "@/app/services/api-urls";

type Listener = () => void;

const listeners = new Set<Listener>();

let isAuthenticatedSnapshot = false;
let refreshPromise: Promise<void> | null = null;
let windowListenersAttached = false;

function emitAuthState(nextValue: boolean) {
  if (isAuthenticatedSnapshot === nextValue) {
    return;
  }

  isAuthenticatedSnapshot = nextValue;
  listeners.forEach((listener) => listener());
}

async function syncAuthState() {
  if (typeof window === "undefined") {
    return;
  }

  if (!hasStoredAuthToken()) {
    emitAuthState(false);
    return;
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      await api.get(API_URLS.IS_AUTHENTICATED);
      emitAuthState(true);
    } catch {
      emitAuthState(false);
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function handleAuthRefresh() {
  void syncAuthState();
}

function handleVisibilityChange() {
  if (document.visibilityState !== "visible") {
    return;
  }

  void syncAuthState();
}

function attachWindowListeners() {
  if (typeof window === "undefined" || windowListenersAttached) {
    return;
  }

  windowListenersAttached = true;
  window.addEventListener(AUTH_CHANGED_EVENT, handleAuthRefresh);
  window.addEventListener("storage", handleAuthRefresh);
  window.addEventListener("focus", handleAuthRefresh);
  document.addEventListener("visibilitychange", handleVisibilityChange);
}

function detachWindowListeners() {
  if (typeof window === "undefined" || !windowListenersAttached) {
    return;
  }

  windowListenersAttached = false;
  window.removeEventListener(AUTH_CHANGED_EVENT, handleAuthRefresh);
  window.removeEventListener("storage", handleAuthRefresh);
  window.removeEventListener("focus", handleAuthRefresh);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  attachWindowListeners();

  if (listeners.size === 1) {
    void syncAuthState();
  }

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0) {
      detachWindowListeners();
    }
  };
}

function getSnapshot() {
  return isAuthenticatedSnapshot;
}

export function useAuthenticatedUser() {
  return React.useSyncExternalStore(subscribe, getSnapshot, () => false);
}